import { Animated, Pressable, StyleSheet } from 'react-native';
import {
	NavigationState,
	Route as RouteTypeLib,
	SceneRendererProps,
	TabBar as TabBarLib,
	TabBarItemProps,
} from 'react-native-tab-view';

import { colors, spacing, shadows } from '../../../theme';
import { Text } from '../../Text';
import { Badge } from '../../Badge';

interface Route extends RouteTypeLib {
	badgeValue?: number;
}
export const TabBar = (
	props: SceneRendererProps & {
		navigationState: NavigationState<Route>;
	},
) => {
	const {
		navigationState: { routes },
	} = props;
	const hasBadge = routes.some((route) => route.badgeValue);

	return (
		<TabBarLib
			{...props}
			style={[styles.tabBar, hasBadge && styles.tabBarWithBadges]}
			gap={spacing()}
			renderTabBarItem={TabBarItem}
			renderIndicator={() => null}
		/>
	);
};

function TabBarItem({
	onPress,
	route,
	labelText,
	navigationState,
	position,
}: TabBarItemProps<Route>) {
	const tabIndex = navigationState.routes.indexOf(route);
	const activeOpacity = getActiveOpacity(position, navigationState.routes.length, tabIndex);

	const TabItem = (
		<Animated.View style={[styles.tab, { backgroundColor: activeOpacity }]}>
			<Pressable onPress={onPress}>
				<Text numberOfLines={1}>{labelText}</Text>
			</Pressable>
		</Animated.View>
	);

	if (!route.badgeValue) {
		return TabItem;
	}

	return (
		<Badge
			containerStyle={styles.badgeContainer}
			badgeStyle={styles.badge}
			invisible={false}
			value={route.badgeValue}
		>
			{TabItem}
		</Badge>
	);
}

const getActiveOpacity = (
	position: Animated.AnimatedInterpolation<number>,
	routesLength: number,
	tabIndex: number,
) => {
	if (routesLength > 1) {
		const inputRange = Array.from({ length: routesLength }, (_, i) => i);

		return position.interpolate({
			inputRange,
			outputRange: inputRange.map((i) => (i === tabIndex ? colors.primary200 : colors.white)),
		});
	} else {
		return colors.white;
	}
};

const TAB_HEIGHT = 44;
const TAB_HEIGHT_WITH_BADGE = TAB_HEIGHT + 10;

const styles = StyleSheet.create({
	tabBar: {
		backgroundColor: colors.transparent,
		height: TAB_HEIGHT,
		marginHorizontal: spacing(),
		...shadows.reset,
	},
	tabBarWithBadges: {
		height: TAB_HEIGHT_WITH_BADGE,
	},
	tab: {
		width: 'auto',
		backgroundColor: colors.white,
		borderRadius: 8,
		minHeight: TAB_HEIGHT,
		alignSelf: 'flex-end',
		paddingVertical: spacing(),
		paddingHorizontal: spacing(2),
	},
	badgeContainer: {
		height: 54,
		justifyContent: 'flex-end',
	},
	badge: {
		top: 10,
		right: -5,
	},
});
